import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { LOCALE_STORAGE_KEY } from "@/utils/constants";
import localeService from "@/services/localeService";

const LOCALE_IP_STORAGE_KEY = `${LOCALE_STORAGE_KEY}_ip`;

export const useLocaleStore = defineStore("locale", () => {
  // State
  const currentLocale = ref("US");
  const isInitialized = ref(false);
  const isLoading = ref(false);
  const error = ref(null);

  // Getters
  const availableLocales = computed(() => [
    { value: "US", text: "United States (US)" },
    { value: "SG", text: "Singapore (SG)" },
  ]);

  const isSingapore = computed(() => currentLocale.value === "SG");
  const isUnitedStates = computed(() => currentLocale.value === "US");

  // Actions
  const detectUserRegion = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await localeService.getIPLocation();

      // Check if the country is Singapore
      const isSingaporeRegion = response.data?.country === "SG";

      const detectedLocale = isSingaporeRegion ? "SG" : "US";
      const userIp = response.data?.ip || null;

      return { locale: detectedLocale, ip: userIp };
    } catch (err) {
      console.warn("Failed to detect user region:", err);
      error.value = err.message;
      // Fallback to US if detection fails
      return { locale: "US", ip: null };
    } finally {
      isLoading.value = false;
    }
  };

  const initializeLocale = async () => {
    if (isInitialized.value) return;

    try {
      // Get saved locale and IP from localStorage
      const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
      const savedIp = localStorage.getItem(LOCALE_IP_STORAGE_KEY);

      // Always detect current region to check if IP changed
      const { locale: detectedLocale, ip: currentIp } =
        await detectUserRegion();

      // If we have a saved locale and the IP hasn't changed, use the saved locale
      if (
        savedLocale &&
        ["US", "SG"].includes(savedLocale) &&
        savedIp &&
        savedIp === currentIp
      ) {
        currentLocale.value = savedLocale;
        isInitialized.value = true;
        return;
      }

      // If IP changed or no saved data, use the newly detected locale
      currentLocale.value = detectedLocale;

      // Save the detected locale and IP to localStorage
      localStorage.setItem(LOCALE_STORAGE_KEY, detectedLocale);
      if (currentIp) {
        localStorage.setItem(LOCALE_IP_STORAGE_KEY, currentIp);
      }
      isInitialized.value = true;
    } catch (err) {
      console.error("Failed to initialize locale:", err);
      // Fallback to US
      currentLocale.value = "US";
      localStorage.setItem(LOCALE_STORAGE_KEY, "US");
      isInitialized.value = true;
    }
  };

  const setLocale = (locale) => {
    if (!["US", "SG"].includes(locale)) {
      console.warn(`Invalid locale: ${locale}. Must be 'US' or 'SG'`);
      return;
    }

    currentLocale.value = locale;
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  };

  const resetToDefault = async () => {
    const { locale: detectedLocale, ip: currentIp } = await detectUserRegion();
    setLocale(detectedLocale);
    if (currentIp) {
      localStorage.setItem(LOCALE_IP_STORAGE_KEY, currentIp);
    }
  };

  const hasSavedPreference = () => {
    const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
    return savedLocale && ["US", "SG"].includes(savedLocale);
  };

  const getLocaleCurrency = () => {
    return currentLocale.value === "US" ? "USD" : "SGD";
  };

  return {
    // State
    currentLocale,
    isInitialized,
    isLoading,
    error,

    // Getters
    availableLocales,
    isSingapore,
    isUnitedStates,

    // Actions
    detectUserRegion,
    initializeLocale,
    setLocale,
    resetToDefault,
    hasSavedPreference,
    getLocaleCurrency,
  };
});
